import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { UserPlus, Search, ArrowRight, Mail, Trash2 } from 'lucide-react';
import { clientsApi } from '../lib/api';
import { useToast } from '../hooks/use-toast';

const StatusBadge = ({ status }) => {
  const map = {
    active: { label: 'Active', cls: 'border-[#7fb685]/40 bg-[#7fb685]/10 text-[#7fb685]' },
    pending: { label: 'Pending', cls: 'border-stone-300 bg-stone-100 text-stone-700' },
    review: { label: 'Review', cls: 'border-[#4b5320]/40 bg-[#4b5320]/10 text-[#4b5320]' },
    completed: { label: 'Complete', cls: 'border-stone-300 bg-stone-100 text-stone-700' },
  };
  const s = map[status] || map.pending;
  return <div className={`text-xs uppercase tracking-widest px-2.5 py-1 rounded-sm border inline-block ${s.cls}`}>{s.label}</div>;
};

const Clients = () => {
  const { toast } = useToast();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', email: '', phone: '', address: '' });

  const load = async () => {
    try {
      const r = await clientsApi.list();
      setClients(r.data);
    } catch (e) {
      toast({ title: 'Error', description: 'Could not load clients' });
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = clients.filter(c => {
    const matchQ = c.name.toLowerCase().includes(query.toLowerCase()) || (c.email || '').toLowerCase().includes(query.toLowerCase());
    const matchF = filter === 'all' || c.status === filter;
    return matchQ && matchF;
  });

  const addClient = async () => {
    if (!newClient.name || !newClient.email) {
      toast({ title: 'Missing info', description: 'Name and email required.' });
      return;
    }
    setLoading(true);
    try {
      await clientsApi.create(newClient);
      setNewClient({ name: '', email: '', phone: '', address: '' });
      setOpen(false);
      toast({ title: 'Client enrolled', description: `${newClient.name} has been added.` });
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail || 'Could not add client' });
    } finally {
      setLoading(false);
    }
  };

  const filters = ['all', 'active', 'pending', 'review', 'completed'];

  const deleteClient = async (e, c) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Permanently delete ${c.name}? This removes the client and ALL their data — disputes, items, documents, insights, share links. Cannot be undone.`)) return;
    try {
      await clientsApi.delete(c.id);
      toast({ title: 'Client deleted', description: `${c.name} and all associated data removed.` });
      load();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || 'Could not delete client' });
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-2">Roster</div>
          <h1 className="font-display text-4xl text-stone-900">Clients</h1>
          <p className="text-sm text-stone-700 mt-2">{filtered.length} of {clients.length} clients</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#4b5320] hover:bg-[#3d4419] text-white font-semibold rounded-sm">
              <UserPlus className="w-4 h-4 mr-2" /> Add Client
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#ffffff] border-stone-200 text-stone-800 rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Enroll New Client</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-700">Full Name</Label>
                <Input value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} className="mt-2 bg-[#fafaf6] border-stone-200 rounded-sm" placeholder="John Doe" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-700">Email</Label>
                <Input type="email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} className="mt-2 bg-[#fafaf6] border-stone-200 rounded-sm" placeholder="john@example.com" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-700">Phone</Label>
                <Input value={newClient.phone} onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} className="mt-2 bg-[#fafaf6] border-stone-200 rounded-sm" placeholder="(555) 555-5555" data-testid="client-phone-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-700">Mailing Address</Label>
                <Input value={newClient.address} onChange={(e) => setNewClient({ ...newClient, address: e.target.value })} className="mt-2 bg-[#fafaf6] border-stone-200 rounded-sm" placeholder="123 Main St, Anytown, ST 12345" data-testid="client-address-input" />
                <div className="text-xs text-stone-500 mt-1">Used as the return address on dispute letters sent to credit bureaus.</div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="border-stone-300 bg-transparent text-stone-700 hover:bg-stone-100 rounded-sm">Cancel</Button>
              <Button disabled={loading} onClick={addClient} className="bg-[#4b5320] hover:bg-[#3d4419] text-white font-semibold rounded-sm">
                {loading ? 'Enrolling...' : 'Enroll Client'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex-1 min-w-[240px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search clients..." className="pl-10 bg-[#fafaf6] border-stone-200 rounded-sm h-10" />
        </div>
        <div className="flex items-center gap-1 border border-stone-200 bg-[#fafaf6] rounded-sm px-1">
          {filters.map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm transition-colors ${
                filter === f ? 'bg-[#4b5320] text-white font-semibold' : 'text-stone-700 hover:text-stone-900'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-stone-200/80 bg-[#ffffff] rounded-sm overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-stone-200/80 text-xs uppercase tracking-widest text-stone-500">
          <div className="col-span-4">Client</div>
          <div className="col-span-2">Score</div>
          <div className="col-span-2">Items</div>
          <div className="col-span-2">Disputes</div>
          <div className="col-span-2 text-right">Status</div>
        </div>
        <div className="divide-y divide-stone-200/80">
          {filtered.map(c => {
            const lift = (c.currentScore || 0) - (c.initialScore || 0);
            return (
              <Link key={c.id} to={`/app/clients/${c.id}`} className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-stone-100/50 transition-colors items-center">
                <div className="col-span-4 flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-[#4b5320]/15 border border-[#4b5320]/30 text-[#4b5320] flex items-center justify-center rounded-sm font-semibold text-sm flex-shrink-0">
                    {c.avatar}
                  </div>
                  <div className="min-w-0">
                    <div className="text-stone-900 text-sm font-medium truncate">{c.name}</div>
                    <div className="text-xs text-stone-500 truncate flex items-center gap-1.5"><Mail className="w-3 h-3" /> {c.email}</div>
                    {(c.emailsSent > 0 || c.reportViews > 0) && (
                      <div
                        className="mt-1 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-[#4b5320] bg-[#4b5320]/8 border border-[#4b5320]/20 rounded-sm px-1.5 py-0.5"
                        data-testid={`client-engagement-${c.id}`}
                        title={`${c.emailsSent} report email(s) sent · viewed ${c.reportViews} time(s)`}
                      >
                        <span>Sent {c.emailsSent}</span>
                        <span className="text-stone-400">·</span>
                        <span>Viewed {c.reportViews}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="font-mono text-sm text-stone-900">{c.currentScore || '—'}</div>
                  {lift > 0 && <div className="text-xs text-[#7fb685]">+{lift}</div>}
                </div>
                <div className="col-span-2 font-mono text-sm">
                  <span className="text-stone-900">{c.itemsRemoved || 0}</span>
                  <span className="text-stone-400">/{c.negativeItems || 0}</span>
                </div>
                <div className="col-span-2 font-mono text-sm text-stone-900">{c.activeDisputes || 0}</div>
                <div className="col-span-2 flex justify-end items-center gap-2">
                  <StatusBadge status={c.status} />
                  <button
                    onClick={(e) => deleteClient(e, c)}
                    className="text-stone-400 hover:text-red-600 p-1 -mr-1"
                    title="Delete client"
                    data-testid={`delete-client-${c.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <ArrowRight className="w-4 h-4 text-stone-400" />
                </div>
              </Link>
            );
          })}
          {filtered.length === 0 && (
            <div className="px-6 py-16 text-center text-stone-500">
              {clients.length === 0 ? 'No clients yet. Click "Add Client" to enroll your first.' : 'No clients match your filters.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Clients;
